import { useMutation } from "react-query";

import { API_URL, isMocksEnabled } from "./helpers";
import { useAppContext } from "../AppProviders";
import type { RequestApi } from "./index";
import {
  getThreeSixtyFinishedListMock,
  getThreeSixtyInvestigatingListMock,
  getThreeSixtyQAListMock,
  getThreeSixtyQueueingListMock,
  getThreeSixtyWebSearchListMock,
} from "../__mocks__/ThreeSixty";
import {
  Insights360,
  ThreeSixtyFinishedRow,
  ThreeSixtyInvestigatingRow,
  ThreeSixtyQARow,
  ThreeSixtyQueueingRow,
  ThreeSixtyWebSearchRow,
} from "./ThreeSixty";

/** JSON shape from ``GET /lists/360/*`` report summary items. */
export type ReportSummaryItemJson = {
  report_uuid?: string | null;
  workflow_run_uuid?: string | null;
  full_name?: string | null;
  client_report_id?: string | null;
  company_uuid?: string | null;
  company_name?: string | null;
  blueprint_uuid?: string | null;
  blueprint_name?: string | null;
  behavior_kit_uuid?: string | null;
  behavior_kit_name?: string | null;
  due_date?: string | null;
  due_date_updated?: boolean | null;
  created_at?: string | null;
  report_date_added?: string | null;
  finish_at?: string | null;
  step_payload?: Record<string, unknown> | null;
  step_name?: string | null;
  locked_by_name?: string | null;
  flags_count_payload?: Record<string, unknown> | null;
  has_rerun?: boolean | null;
};

type ReportSummaryListResponseJson = {
  report_summaries?: ReportSummaryItemJson[] | null;
  total_rows?: number | null;
};

const LISTS_PAGE_SIZE = 100;

function isoOrEmpty(value: string | null | undefined): string {
  if (value == null || value === "") {
    return "";
  }
  return value;
}

function readCheckTypeFromStepPayload(sp: Record<string, unknown>): {
  check_type_uuid: string;
  check_type_name: string;
} {
  const checkTypeUuid =
    (typeof sp.check_type_uuid === "string" && sp.check_type_uuid) ||
    (typeof sp.check_type_external_id === "string" &&
      sp.check_type_external_id) ||
    "";
  const checkTypeName =
    (typeof sp.check_type_name === "string" && sp.check_type_name) || "";
  return { check_type_uuid: checkTypeUuid, check_type_name: checkTypeName };
}

/**
 * Aligns with workflow ``convertToQueuing`` / ``convertToInvestigating`` / ``convertToQA``:
 * ``check_type_uuid`` is the workflow blueprint id; ``check_type_name`` comes from the
 * **step** payload's ``blueprint_name`` (not ``check_type_name``). Report summaries also
 * expose ``blueprint_name`` / ``blueprint_uuid`` after augmentation when step payload
 * omits those keys.
 */
function resolveCheckTypeFor360ListRow(
  item: ReportSummaryItemJson
): { check_type_uuid: string; check_type_name: string } {
  const sp = item.step_payload ?? {};
  const fromKeys = readCheckTypeFromStepPayload(sp);
  const stepBlueprintName =
    typeof sp.blueprint_name === "string" ? sp.blueprint_name : "";
  const check_type_name =
    fromKeys.check_type_name ||
    stepBlueprintName ||
    (item.blueprint_name ?? "") ||
    "";
  const check_type_uuid =
    fromKeys.check_type_uuid ||
    (item.blueprint_uuid != null ? String(item.blueprint_uuid) : "");
  return { check_type_uuid, check_type_name };
}

function toIsoTimestamp(v: unknown): string {
  if (v == null || v === "") return "";
  if (typeof v === "string") return v;
  return String(v);
}

function mapFlagsToInsights(
  fcp: Record<string, unknown> | null | undefined
): Insights360 | undefined {
  if (!fcp || typeof fcp !== "object") return undefined;
  return {
    total: Number(fcp.total ?? 0),
    posts: Number(fcp.posts_count ?? fcp.posts ?? 0),
    articles: Number(fcp.articles_count ?? fcp.articles ?? 0),
    news: Number(fcp.news ?? 0),
    legal: Number(fcp.legal ?? 0),
    keywords: Number(fcp.keywords ?? 0),
    topics: Number(fcp.topics ?? 0),
    community: Number(fcp.community_count ?? fcp.community ?? 0),
    adverse: Number(fcp.adverse_flag_count ?? fcp.adverse ?? 0),
  };
}

function mapToBaseFields(item: ReportSummaryItemJson) {
  const ct = resolveCheckTypeFor360ListRow(item);
  const requestedAt = toIsoTimestamp(
    item.report_date_added ?? item.created_at
  );
  return {
    candidate_name: item.full_name ?? "",
    workflow_run_uuid:
      item.workflow_run_uuid != null ? String(item.workflow_run_uuid) : "",
    request_uuid: item.report_uuid != null ? String(item.report_uuid) : "",
    company_uuid: item.company_uuid != null ? String(item.company_uuid) : "",
    company_name: item.company_name ?? "",
    check_type_uuid: ct.check_type_uuid,
    check_type_name: ct.check_type_name,
    behavior_kit_uuid:
      item.behavior_kit_uuid != null ? String(item.behavior_kit_uuid) : "",
    behavior_kit_name: item.behavior_kit_name ?? "",
    requested_at: requestedAt,
    due_date: isoOrEmpty(item.due_date ?? undefined),
    due_date_updated: Boolean(item.due_date_updated),
    custom_id: item.client_report_id ?? "",
    ...(item.has_rerun != null ? { has_rerun: item.has_rerun } : {}),
  };
}

const QUEUING_LISTS360_DEFAULT_STATUS = "360Review";

export function mapReportSummaryToQueueingRow(
  item: ReportSummaryItemJson
): ThreeSixtyQueueingRow {
  const fromList = (item.step_name ?? "").trim();
  return {
    ...mapToBaseFields(item),
    misconduct_step: fromList || QUEUING_LISTS360_DEFAULT_STATUS,
  };
}

export function mapReportSummaryToWebSearchRow(
  item: ReportSummaryItemJson
): ThreeSixtyWebSearchRow {
  return {
    ...mapToBaseFields(item),
    locked_by: (item.locked_by_name ?? "").trim(),
  };
}

export function mapReportSummaryToInvestigatingRow(
  item: ReportSummaryItemJson
): ThreeSixtyInvestigatingRow {
  return {
    ...mapToBaseFields(item),
    locked_by: (item.locked_by_name ?? "").trim(),
  };
}

export function mapReportSummaryToQARow(
  item: ReportSummaryItemJson
): ThreeSixtyQARow {
  return {
    ...mapToBaseFields(item),
    locked_by: (item.locked_by_name ?? "").trim(),
  };
}

export function mapReportSummaryToFinishedRow(
  item: ReportSummaryItemJson
): ThreeSixtyFinishedRow {
  const insights = mapFlagsToInsights(item.flags_count_payload ?? undefined);
  return {
    request_uuid: item.report_uuid != null ? String(item.report_uuid) : "",
    name: item.full_name ?? "",
    custom_id: item.client_report_id ?? "",
    due_date: isoOrEmpty(item.due_date ?? undefined),
    finished_at:
      item.finish_at != null ? isoOrEmpty(item.finish_at) : null,
    company_uuid: item.company_uuid != null ? String(item.company_uuid) : "",
    company_name: item.company_name ?? "",
    blueprint_uuid:
      item.blueprint_uuid != null ? String(item.blueprint_uuid) : "",
    blueprint_name: item.blueprint_name ?? "",
    behavior_kit_uuid:
      item.behavior_kit_uuid != null ? String(item.behavior_kit_uuid) : null,
    behavior_kit_name: item.behavior_kit_name ?? null,
    ...(insights ? { insights } : {}),
    ...(item.has_rerun != null ? { has_rerun: item.has_rerun } : {}),
  };
}

async function fetchAllReportSummaries(
  requestApi: RequestApi,
  listsPath: string
): Promise<ReportSummaryItemJson[]> {
  const full: ReportSummaryItemJson[] = [];
  let offset = 0;
  const urlBase = `${API_URL}${listsPath}`;
  while (true) {
    const response = await requestApi.get(
      `${urlBase}?offset=${offset}&size=${LISTS_PAGE_SIZE}`
    );
    const body = response.data as ReportSummaryListResponseJson;
    const summaries = body.report_summaries ?? [];
    for (const row of summaries) {
      full.push(row);
    }
    if (summaries.length === 0) {
      break;
    }
    offset += summaries.length;
    const totalRows = body.total_rows;
    if (typeof totalRows === "number" && full.length >= totalRows) {
      break;
    }
    if (totalRows == null && summaries.length < LISTS_PAGE_SIZE) {
      break;
    }
  }
  return full;
}

function useLists360Mutation<T>(
  listsPath: string,
  mapRow: (item: ReportSummaryItemJson) => T,
  mockLoader: () => Promise<T[]>
) {
  const { requestApi } = useAppContext();
  const load = async (_force: boolean): Promise<T[]> => {
    if (isMocksEnabled()) {
      return mockLoader();
    }
    const items = await fetchAllReportSummaries(requestApi, listsPath);
    return items.map(mapRow);
  };
  return useMutation((force: boolean) => load(force), {});
}

export function useGetThreeSixtyQueueingListLists360() {
  return useLists360Mutation(
    "/lists/360/queuing",
    mapReportSummaryToQueueingRow,
    getThreeSixtyQueueingListMock
  );
}

export function useGetThreeSixtyWebSearchListLists360() {
  return useLists360Mutation(
    "/lists/360/web_search",
    mapReportSummaryToWebSearchRow,
    getThreeSixtyWebSearchListMock
  );
}

export function useGetThreeSixtyInvestigatingListLists360() {
  return useLists360Mutation(
    "/lists/360/investigating",
    mapReportSummaryToInvestigatingRow,
    getThreeSixtyInvestigatingListMock
  );
}

export function useGetThreeSixtyQAListLists360() {
  return useLists360Mutation(
    "/lists/360/qa",
    mapReportSummaryToQARow,
    getThreeSixtyQAListMock
  );
}

export function useGetThreeSixtyFinishedListLists360() {
  const { requestApi } = useAppContext();
  const load = async (_force: boolean): Promise<ThreeSixtyFinishedRow[]> => {
    if (isMocksEnabled()) {
      const mock = await getThreeSixtyFinishedListMock();
      return mock[1];
    }
    const items = await fetchAllReportSummaries(
      requestApi,
      "/lists/360/finished"
    );
    return items.map(mapReportSummaryToFinishedRow);
  };
  return useMutation((force: boolean) => load(force), {});
}
